import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, Clock, LogIn, LogOut, MapPin, X, Loader2, CheckCircle2 } from "lucide-react";
import API, { extractApiError } from "../services/api";
import { useToast } from "../hooks/useToast";
import { useModules } from "../theme/ModuleContext";

/**
 * Check In / Check Out from the website.
 *
 * The fallback for an employee whose phone is not working. It calls the same
 * endpoints as the mobile app (/attendance/check-in, /attendance/check-out), so
 * every server rule still applies: working days, roster, geo-fence, shift
 * status (ON_TIME / LATE / HALF_DAY), selfie and face/liveness checks as set in
 * Settings → Attendance. Nothing is relaxed for the web.
 *
 *   • Location comes from the browser (the user must allow it).
 *   • When a selfie is required it is taken with the computer's webcam; with
 *     liveness on, one photo per challenge step, as on the phone.
 */

const asBool = (v, fallback) => {
  if (v === true || v === false) return v;
  if (v == null || v === "") return fallback;
  return String(v).toLowerCase() === "true" || String(v) === "1";
};

function getBrowserLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("This browser cannot share your location."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => {
        if (err.code === 1) {
          reject(new Error("Location permission was denied. Allow location for this site in your browser (padlock icon in the address bar), then try again."));
        } else {
          reject(new Error("Could not get your location. Check that location services are on and try again."));
        }
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  });
}

function captureFrame(video) {
  return new Promise((resolve, reject) => {
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) { reject(new Error("The camera is not ready yet.")); return; }
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(video, 0, 0, w, h);
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Could not take the photo."))), "image/jpeg", 0.9);
  });
}

export default function WebAttendance() {
  const { isModuleEnabled } = useModules();
  const enabled = isModuleEnabled("attendance");
  const [status, setStatus] = useState(null); // { checkedIn, workDate, shift, shiftTiming, rosteredOff }
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await API.get("/attendance/today-status");
      setStatus(res.data || null);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    if (enabled) refresh();
  }, [enabled, refresh]);

  if (!enabled || !status) return null;
  const checkedIn = Boolean(status.checkedIn);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={checkedIn ? "Check out from the website" : "Check in from the website"}
        className={[
          "inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 sm:px-3 text-sm font-semibold text-white transition active:scale-95",
          checkedIn ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700",
        ].join(" ")}
      >
        {checkedIn ? <LogOut size={16} /> : <LogIn size={16} />}
        <span className="hidden sm:inline">{checkedIn ? "Check Out" : "Check In"}</span>
      </button>
      {open && (
        <AttendanceDialog
          checkedIn={checkedIn}
          status={status}
          onClose={() => { setOpen(false); refresh(); }}
        />
      )}
    </>
  );
}

function AttendanceDialog({ checkedIn, status, onClose }) {
  const toast = useToast();
  const action = checkedIn ? "check-out" : "check-in";
  const label = checkedIn ? "Check Out" : "Check In";

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [phase, setPhase] = useState("loading"); // loading | camera | saving | error
  const [error, setError] = useState("");
  const [location, setLocation] = useState(null);
  const [needSelfie, setNeedSelfie] = useState(false);
  const [liveness, setLiveness] = useState(null); // { challengeId, steps, instructions }
  const [frames, setFrames] = useState([]);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => () => stopCamera(), []);

  // Prepare: settings, location, and (when required) camera + liveness challenge.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [statusRes, settingsRes] = await Promise.all([
          API.get("/attendance/today-status"),
          API.get("/settings/attendance").catch(() => ({ data: {} })),
        ]);
        if (Boolean(statusRes.data?.checkedIn) !== checkedIn) {
          throw new Error(checkedIn
            ? "You are already checked out. Close this window to refresh."
            : "You are already checked in. Close this window to refresh.");
        }
        const s = settingsRes.data || {};
        const selfie = checkedIn
          ? asBool(s.SELFIE_REQUIRED_CHECK_OUT, true)
          : asBool(s.SELFIE_REQUIRED_CHECK_IN, true);
        const live = selfie && asBool(s.FACE_RECOGNITION_ENABLED, false) && asBool(s.FACE_LIVENESS_ENABLED, false);

        const loc = await getBrowserLocation();
        if (cancelled) return;
        setLocation(loc);

        if (!selfie) {
          setNeedSelfie(false);
          setPhase("camera");
          return;
        }
        if (live) {
          const res = await API.get("/attendance/liveness-challenge");
          const d = res.data || {};
          if (!d.challengeId || !Array.isArray(d.steps) || !d.steps.length) {
            throw new Error("Could not start the liveness check. Please try again.");
          }
          if (cancelled) return;
          setLiveness({
            challengeId: d.challengeId,
            steps: d.steps,
            instructions: d.steps.map((st, i) => d.instructions?.[i] || st),
          });
        }
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("This browser cannot use the camera. A selfie is required to " + action + ".");
        }
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
        } catch (e) {
          throw new Error(e?.name === "NotAllowedError"
            ? "Camera permission was denied. Allow the camera for this site in your browser, then try again."
            : "No camera was found. A selfie is required to " + action + ".");
        }
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        setNeedSelfie(true);
        setPhase("camera");
      } catch (e) {
        if (!cancelled) { setError(extractApiError(e, e.message)); setPhase("error"); }
      }
    })();
    return () => { cancelled = true; };
  }, [checkedIn, action]);

  // Attach the stream once the <video> is on screen.
  useEffect(() => {
    if (phase === "camera" && needSelfie && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [phase, needSelfie]);

  const totalShots = liveness ? liveness.steps.length : 1;
  const nextInstruction = liveness ? liveness.instructions[frames.length] : "Look straight at the camera";
  const ready = !needSelfie || frames.length >= totalShots;

  const takePhoto = async () => {
    try {
      const blob = await captureFrame(videoRef.current);
      setFrames((f) => [...f, blob]);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const submit = async () => {
    setPhase("saving");
    try {
      const form = new FormData();
      form.append("lat", String(location.lat));
      form.append("lng", String(location.lng));
      form.append("address", "");
      if (needSelfie) {
        if (liveness) {
          form.append("challengeId", liveness.challengeId);
          form.append("challenge", liveness.steps.join(","));
          frames.forEach((b, i) => form.append("frames", b, `frame_${i}.jpg`));
        } else {
          form.append("image", frames[0], "selfie.jpg");
        }
      }
      const res = await API.post(`/attendance/${action}`, form, { timeout: 120000 });
      stopCamera();
      toast.success(typeof res.data === "string" && res.data ? res.data : `${label} saved`);
      onClose();
    } catch (e) {
      // A liveness challenge is single-use; a retry needs a fresh one.
      setError(extractApiError(e, `${label} failed`));
      setPhase("error");
      stopCamera();
    }
  };

  /* Rendered into <body> through a portal. The header this button lives in
   * uses backdrop-blur, and an element with a backdrop-filter becomes the
   * containing block for position:fixed descendants — so a "fixed inset-0"
   * overlay inside the header was sized to the 64px header, and the dialog was
   * centred on it with its top half cut off above the screen. */
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center overflow-y-auto bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="my-auto w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto rounded-2xl bg-white p-5 shadow-xl dark:bg-slate-900">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-white">
            <Clock size={18} className="text-cyan-600" /> {label}
          </h2>
          <button type="button" onClick={() => { stopCamera(); onClose(); }} aria-label="Close"
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-slate-800">
            <X size={18} />
          </button>
        </div>

        {status?.shift && (
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            {status.shift}{status.shiftTiming ? ` · ${status.shiftTiming}` : ""}
          </p>
        )}

        {phase === "loading" && (
          <p className="flex items-center gap-2 py-6 text-sm text-gray-600 dark:text-gray-300">
            <Loader2 size={16} className="animate-spin" /> Getting your location and checking requirements…
          </p>
        )}

        {phase === "error" && (
          <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
            {error}
          </div>
        )}

        {(phase === "camera" || phase === "saving") && (
          <div className="space-y-3">
            {location && (
              <p className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300">
                <MapPin size={13} /> Location captured
                {Number.isFinite(location.accuracy) ? ` (±${Math.round(location.accuracy)} m)` : ""}
              </p>
            )}

            {needSelfie && (
              <>
                <div className="relative overflow-hidden rounded-xl bg-black">
                  <video ref={videoRef} playsInline muted className="aspect-[4/3] max-h-[45vh] w-full -scale-x-100 object-cover" />
                  {!ready && (
                    <div className="absolute inset-x-0 bottom-0 bg-black/60 px-3 py-2 text-center text-sm font-semibold text-white">
                      {nextInstruction}
                      {totalShots > 1 && <span className="ml-1 font-normal opacity-80">({frames.length + 1}/{totalShots})</span>}
                    </div>
                  )}
                  {ready && (
                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 bg-emerald-700/80 px-3 py-2 text-sm font-semibold text-white">
                      <CheckCircle2 size={16} /> {totalShots > 1 ? "All photos taken" : "Photo taken"}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {!ready && (
                    <button type="button" onClick={takePhoto} disabled={phase === "saving"}
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:text-gray-200 dark:hover:bg-slate-800">
                      <Camera size={16} /> Take photo
                    </button>
                  )}
                  {frames.length > 0 && phase !== "saving" && (
                    <button type="button" onClick={() => setFrames([])}
                      className="rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-800">
                      Retake
                    </button>
                  )}
                </div>
              </>
            )}

            <button type="button" onClick={submit} disabled={!ready || phase === "saving"}
              className={[
                "inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50",
                checkedIn ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700",
              ].join(" ")}>
              {phase === "saving" ? <Loader2 size={16} className="animate-spin" /> : (checkedIn ? <LogOut size={16} /> : <LogIn size={16} />)}
              {phase === "saving" ? "Saving…" : label}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
